import dayjs from "dayjs";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";

import type { ScheduledPost } from "../scheduledPost";

import { formatTimeZone } from "../formatTimeZone";
import { POST_STATUS_BADGES } from "../postStatusBadges";
import { PlatformPostPreview } from "./PlatformPostPreview";

export function PostPreview({
  onClose,
  onEdit,
  onStep,
  post,
}: {
  onClose: () => void;
  onEdit: () => void;
  onStep: (direction: -1 | 1) => void;
  post: ScheduledPost;
}) {
  const scheduledAt = dayjs(post.scheduledAt);
  const statusBadge = POST_STATUS_BADGES[post.status];

  return (
    <aside
      aria-label="Post preview"
      className="flex w-[360px] shrink-0 flex-col overflow-hidden rounded-xl border border-border-secondary bg-bg-primary"
    >
      <header className="flex flex-col gap-xs border-b border-border-secondary p-lg">
        <div className="flex items-center justify-between">
          <Badge size="small" variant={statusBadge.variant}>
            {statusBadge.label}
          </Badge>
          <div className="flex items-center gap-xs">
            <Button
              aria-label="Previous post"
              color="gray"
              isIconOnly
              onPress={() => onStep(-1)}
              variant="unstyled"
            >
              <AccessibleIcon>
                <ChevronLeft className="text-fg-quaternary" />
              </AccessibleIcon>
            </Button>
            <Button
              aria-label="Next post"
              color="gray"
              isIconOnly
              onPress={() => onStep(1)}
              variant="unstyled"
            >
              <AccessibleIcon>
                <ChevronRight className="text-fg-quaternary" />
              </AccessibleIcon>
            </Button>
            <span className="mx-xs h-5 w-px bg-border-secondary" />
            <Button
              aria-label="Close preview"
              color="gray"
              isIconOnly
              onPress={onClose}
              variant="unstyled"
            >
              <AccessibleIcon>
                <X className="text-fg-quaternary" />
              </AccessibleIcon>
            </Button>
          </div>
        </div>
        <h2
          className="truncate text-lg font-semibold text-text-primary"
          title={post.title}
        >
          {post.title}
        </h2>
        <p className="text-sm text-text-secondary">
          {scheduledAt.format("ddd, MMM D, YYYY · h:mm A")}
        </p>
        <p className="text-xs text-text-tertiary">
          Time zone: {formatTimeZone()}
        </p>
        {post.status === "failed" && post.errorMessage && (
          <p className="text-sm text-text-error-primary">{post.errorMessage}</p>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-lg">
        <PlatformPostPreview post={post} />
      </div>

      {post.status === "scheduled" && (
        <footer className="border-t border-border-secondary p-lg">
          <Button
            className="w-full"
            color="gray"
            onPress={onEdit}
            variant="secondary"
          >
            Edit schedule
          </Button>
        </footer>
      )}
    </aside>
  );
}
